<template>
  <Multiselector
    v-bind="$attrs"
    :id="getRandomId('diets-select')"
    :disabled="loading || disabled"
    :model-value="selectedValues"
    :options="options"
    :placeholder="loading ? $t('Loading') : $t('Choose diets')"
    :main-input-label="hideLabel ? '' : (label ?? $t('Diets'))"
    :multiple="multiple"
    :label-class="labelClass"
    :container-class="containerClass"
    @update:model-value="updateSelected"
  />
</template>

<script>
import Multiselector from "@/components/Multiselector.vue";
import { getRandomId } from "@/utils/string";

export default {
  name: "DietsSelect",

  components: {
    Multiselector,
  },

  props: {
    modelValue: {
      type: [Number, Array],
      default: null,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: String,
      default: "all",
      validator: function (value) {
        return ["all", "select"].includes(value);
      },
    },

    hideLabel: {
      type: Boolean,
      default: false,
    },

    defaultOptions: {
      type: [Array, null],
      default: null,
    },

    personal: {
      type: Boolean,
      default: null,
    },

    autoSelectDefault: {
      type: Boolean,
      default: false,
    },

    institutionId: {
      type: [Number, String],
      default: null,
    },

    // Preferred over institutionId when multiple institutions are relevant (e.g. a price list
    // assigned to several facilities). When both are set, institutionsIds wins.
    institutionsIds: {
      type: Array,
      default: null,
    },

    labelClass: {
      type: String,
      default: null,
    },

    containerClass: {
      type: String,
      default: null,
    },

    label: {
      type: String,
      default: null,
    },

    disablePlaceholder: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    // Shown instead of the normal placeholder while disabled (e.g. "Select institution first" -
    // options here are typically institution-scoped, so a plain "Select diet" would be misleading).
    disabledPlaceholder: {
      type: String,
      default: null,
    },
  },

  emits: ["update:modelValue"],

  data() {
    return {
      allOptions: [],
      loading: false,
    };
  },

  computed: {
    selectedValues() {
      return this.modelValue ? (this.multiple ? this.modelValue : [this.modelValue]) : [];
    },

    placeholderText() {
      if (this.disabled && this.disabledPlaceholder) {
        return this.disabledPlaceholder;
      }

      return this.placeholder === "all" ? this.$t("all") : this.$t("Select diet");
    },

    // Personal (imienne) diets are kept out of the main list and shown under their own header,
    // so "select all" and "N others" summaries read naturally instead of mixing the two kinds.
    groupedAllOptions() {
      const nonPersonalOptions = this.allOptions.filter((option) => !option.personal);
      const personalOptions = this.allOptions.filter((option) => option.personal);

      if (!personalOptions.length) {
        return this.allOptions;
      }

      return [
        ...nonPersonalOptions,
        { value: "personal-diets-group", text: this.$t("Personal diets"), isGroupHeader: true },
        ...personalOptions,
      ];
    },

    options() {
      const options = [...this.groupedAllOptions];

      if (!this.multiple && !this.defaultOptions?.length) {
        options.unshift({
          value: null,
          text: this.placeholderText,
          disabled: this.disablePlaceholder,
        });
      }

      return options;
    },
  },

  watch: {
    defaultOptions(newVal) {
      if (newVal) {
        this.allOptions = newVal;
      }

      if (!this.modelValue && this.autoSelectDefault) {
        this.selectDefaultOption();
      } else {
        this.checkIfSelectedExists();
      }
    },

    async institutionId() {
      await this.refetchOptionsUnlessDefaultOptionsProvided();
    },

    institutionsIds: {
      deep: true,
      async handler() {
        await this.refetchOptionsUnlessDefaultOptionsProvided();
      },
    },
  },

  async mounted() {
    await this.fetchOptions();

    if (!this.modelValue && this.autoSelectDefault) {
      this.selectDefaultOption();
    }
  },

  methods: {
    getRandomId,

    async fetchOptions() {
      const criteria = {};

      if (this.personal !== null) {
        criteria.personal = this.personal;
      }

      if (this.institutionsIds?.length) {
        criteria.institutionsIds = this.institutionsIds;
      } else if (this.institutionId) {
        criteria.institutionId = this.institutionId;
      }

      this.loading = true;
      try {
        this.allOptions = this.defaultOptions || [
          ...(await this.$store.dispatch("diets/getOptions", criteria)),
        ];
      } finally {
        this.loading = false;
      }
    },

    async refetchOptionsUnlessDefaultOptionsProvided() {
      if (this.defaultOptions) {
        return;
      }

      await this.fetchOptions();

      if (!this.modelValue && this.autoSelectDefault) {
        this.selectDefaultOption();
      } else {
        this.checkIfSelectedExists();
      }
    },

    selectDefaultOption() {
      const defaultDiet = this.options.find((item) => item.default);
      if (!defaultDiet) {
        return;
      }

      this.updateSelected(defaultDiet.value);
    },

    checkIfSelectedExists() {
      if (
        !Array.isArray(this.modelValue) &&
        !this.options.some((item) => item.value === this.modelValue)
      ) {
        this.selectDefaultOption();
      }

      if (!Array.isArray(this.modelValue)) {
        return;
      }

      const availableDietsIds = this.modelValue.filter((id) =>
        this.options.some((item) => item.value === id),
      );
      this.updateSelected(availableDietsIds);
    },

    updateSelected(values) {
      if (!this.multiple && Array.isArray(values)) {
        values = values[0];
      }

      this.$emit("update:modelValue", values);
    },
  },
};
</script>
